import { LabelConfigProjector } from '@protocols/domain';
import { LabelConfig, LabelConfigRepository } from '@telespot/domain';

export interface SaveLabelConfigInput {
  configId?: string;
  labelUuid: string;
  methodTypeId: string;
  color: string;
}

export enum SaveLabelConfigErrorCode {
  MISSING_LABEL_UUID = 'A labelUuid is required to save a label config.',
  MISSING_METHOD_TYPE_ID = 'A methodTypeId is required to save a label config.',
  MISSING_COLOR = 'A color is required to save a label config.',
  MISSING_PERSISTED_ID = 'LabelConfigRepository.save() returned an entity without an id.',
}

export class SaveLabelConfigException extends Error {
  constructor(public readonly code: SaveLabelConfigErrorCode) {
    super(code.toString());
    this.name = 'SaveLabelConfigException';
    Object.setPrototypeOf(this, SaveLabelConfigException.prototype);
  }
}

export interface ISaveLabelConfigCommand {
  /**
   * Upserts the colour of a label on a method type, returning the id of the row
   * written. A `configId` the caller already holds is used as-is; otherwise the
   * (method type, label) pair is looked up and its existing row updated, so a
   * caller that does not know the id does not mint a duplicate.
   */
  execute(input: SaveLabelConfigInput): Promise<string>;
}

export class SaveLabelConfigCommand implements ISaveLabelConfigCommand {
  constructor(private readonly labelConfigRepository: LabelConfigRepository) {}

  public async execute(input: SaveLabelConfigInput): Promise<string> {
    if (!input.labelUuid?.trim()) {
      throw new SaveLabelConfigException(SaveLabelConfigErrorCode.MISSING_LABEL_UUID);
    }
    if (!input.methodTypeId?.trim()) {
      throw new SaveLabelConfigException(SaveLabelConfigErrorCode.MISSING_METHOD_TYPE_ID);
    }
    if (!input.color?.trim()) {
      throw new SaveLabelConfigException(SaveLabelConfigErrorCode.MISSING_COLOR);
    }

    const configId = input.configId ?? (await this.findConfigIdForPair(input.methodTypeId, input.labelUuid));

    const entity = new LabelConfig({
      id: configId,
      color: input.color,
      label: input.labelUuid,
      methodTypeId: input.methodTypeId,
    });

    const saved = await this.labelConfigRepository.save(entity);
    if (!saved.id) {
      throw new SaveLabelConfigException(SaveLabelConfigErrorCode.MISSING_PERSISTED_ID);
    }
    return saved.id;
  }

  /**
   * Id of the row this pair already has, or `undefined` when it has none.
   *
   * Where the pair already carries duplicates, this targets the row the read
   * paths treat as authoritative, so the write and the reads agree.
   *
   * NOT A UNIQUENESS GUARANTEE. This is a read followed by a write, so two
   * writers that both find nothing will both create — the window is narrowed,
   * not closed. Real enforcement needs a server-side save hook or a unique index
   * at the storage layer (the typed schema API cannot express one), which is
   * deliberately out of scope here.
   */
  private async findConfigIdForPair(methodTypeId: string, labelUuid: string): Promise<string | undefined> {
    const existing = await this.labelConfigRepository.findByMethodTypeAndLabel(methodTypeId, labelUuid);

    return LabelConfigProjector.groupByMethodType(existing).get(methodTypeId)?.id;
  }
}
